// ============================================================================
// LITIGATION CASE SUMMARY SECTION
// ============================================================================
// Case dashboard — universal cards + case-type-conditional cards. Per-case-
// type variance handled by SUMMARY_CARDS_BY_TYPE map. Staff assignments live
// in the dedicated Assignments section under Overview.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import StatusPill from 'components/common/StatusPill';
import notificationService from 'services/notificationService';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import { litigationCaseService } from 'api/services/litigationCaseService';
import type { LitigationCase, LitigationCaseTypeCode } from 'api/types/litigationCase.types';
import CaseSecuritiesPanel from './CaseSecuritiesPanel';

interface SummarySectionProps {
  kase: LitigationCase;
  /** Optional refresh callback fired after Case Notes are saved. */
  onMutated?: () => void;
}

// ----------------------------------------------------------------------------
// Per-case-type configuration
// ----------------------------------------------------------------------------

interface CaseTypeSummaryConfig {
  /** Show the "Court Reference" card (Court Case #, Filing Date, Subtype, Filed Amount).
   *  Only relevant when the case has been filed in court. */
  courtReference: boolean;
}

const SUMMARY_CARDS_BY_TYPE: Record<LitigationCaseTypeCode, CaseTypeSummaryConfig> = {
  NI_ACT_138:   { courtReference: true  },
  CIVIL_SUIT:   { courtReference: true  },
  EP:           { courtReference: true  },
  ARBITRATION:  { courtReference: false },  // arbitration ref lives in its own section
  SARFAESI:     { courtReference: false },  // only on DRT escalation — separate panel
  VEHICLE_REPO: { courtReference: false },  // never in court
  AUCTION:      { courtReference: false },  // case-group orchestrated, not court
};

const SummarySection: React.FC<SummarySectionProps> = ({ kase, onMutated }) => {
  const cfg = SUMMARY_CARDS_BY_TYPE[kase.caseTypeCode]
    ?? { courtReference: false };

  const fields: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Case Type',       value: kase.caseTypeDisplayName ?? kase.caseTypeCode },
    { label: 'Case Code',       value: kase.caseCode },
    { label: 'Account #',       value: kase.accountNumber ?? '—' },
    { label: 'Customer',        value: kase.customerName ?? '—' },
    { label: 'Product',         value: kase.productCode ?? '—' },
    { label: 'Branch',          value: kase.branchCode ?? '—' },
    { label: 'DPD (now)',       value: String(kase.currentDpd ?? 0) },
    { label: 'Outstanding (now)', value: `₹${(kase.currentOutstanding ?? 0).toLocaleString('en-IN')}` },
    { label: 'Secured',
      value: kase.isSecured
        ? <StatusPill label="Yes" tone="info"    size="xs" />
        : <StatusPill label="No"  tone="neutral" size="xs" /> },
    { label: 'Workflow State',  value: kase.currentStateDisplayName ?? kase.currentStateCode ?? '—' },
    { label: 'Assigned Agent',  value: kase.assignedAgentName ?? '—' },
    { label: 'Opened',          value: kase.openedAt ? formatDate(kase.openedAt) : '—' },
  ];
  if (kase.claimedAt) fields.push({ label: 'Claimed', value: formatDate(kase.claimedAt) });
  if (kase.closedAt)  fields.push({ label: 'Closed',  value: formatDate(kase.closedAt) });
  if (kase.closureReasonName)
    fields.push({ label: 'Closure Reason', value: kase.closureReasonName });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ---- Identity / Summary field grid (universal) ---- */}
      <SectionCard title="Account & Customer Summary">
        <FieldGrid fields={fields} />
      </SectionCard>

      {/* ---- Court Reference (only for court-filed case types) ---- */}
      {cfg.courtReference && (
        <SectionCard title="Court Reference">
          <FieldGrid fields={[
            { label: 'Court Case #',     value: kase.courtCaseNumber ?? '—' },
            { label: 'Filing Date',      value: kase.filingDate ? formatDate(kase.filingDate) : '—' },
            { label: 'Subtype',          value: kase.caseSubtype ?? '—' },
            { label: 'Filed Amount',
              value: kase.caseFiledAmount != null
                ? `₹${kase.caseFiledAmount.toLocaleString('en-IN')}`
                : <span style={{ color: 'var(--form-border-error, #ef4444)' }} title="Required field — set via Edit">— required</span> },
          ]} />
        </SectionCard>
      )}

      {/* ---- Security Details (universal — auto-hides for unsecured cases) ---- */}
      <SectionCard title="Security Details">
        <CaseSecuritiesPanel caseId={kase.id} />
      </SectionCard>

      {/* ---- Case Notes (universal — replaces former Case Brief) ---- */}
      <CaseNotesCard caseId={kase.id} notes={kase.caseBrief ?? null} onSaved={onMutated} />
    </div>
  );
};

// ----------------------------------------------------------------------------
// Local sub-components
// ----------------------------------------------------------------------------

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}> = ({ title, children, trailing }) => (
  <div style={{
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    background: 'var(--bg-primary)',
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '0.5rem 0.85rem',
      borderBottom: '1px solid var(--border-color)',
      fontSize: '0.75rem', fontWeight: 600,
      color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      background: 'var(--bg-secondary)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '0.5rem',
    }}>
      <span>{title}</span>
      {trailing}
    </div>
    <div style={{ padding: '0.85rem' }}>{children}</div>
  </div>
);

// ---- Field grid: reusable label/value 3-4 col grid ----
const FieldGrid: React.FC<{ fields: Array<{ label: string; value: React.ReactNode }> }> = ({ fields }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.5rem 1rem',
  }}>
    {fields.map(f => (
      <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', minWidth: 0 }}>
        <span style={{
          fontSize: '0.65rem', fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{f.label}</span>
        <span style={{
          fontSize: '0.85rem',
          color: 'var(--text-primary)',
          fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{f.value}</span>
      </div>
    ))}
  </div>
);

// ---- Case Notes card with expand/collapse + inline edit ----
const CaseNotesCard: React.FC<{
  caseId: string;
  notes: string | null;
  onSaved?: () => void;
}> = ({ caseId, notes, onSaved }) => {
  const permissions = useRecsuitPermissions();
  const canEdit = permissions.hasAction('litigation-case', 'edit_meta');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Reset the draft when entering edit mode OR when the saved notes change
  // from outside (e.g. another user edited and we refetched).
  useEffect(() => {
    if (!editing) setDraft(notes ?? '');
  }, [notes, editing]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await litigationCaseService.updateNotes(caseId, draft.trim() ? draft : null);
      notificationService.toast.success('Case notes saved.');
      setEditing(false);
      onSaved?.();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to save notes.',
      );
    } finally {
      setSaving(false);
    }
  }, [caseId, draft, onSaved]);

  const handleCancel = useCallback(() => {
    setDraft(notes ?? '');
    setEditing(false);
  }, [notes]);

  const hasNotes = !!(notes && notes.trim().length > 0);
  const isLong = hasNotes && (notes!.length > 280 || notes!.split('\n').length > 3);
  const showFull = expanded || !isLong;

  // ----- Edit mode -----
  if (editing) {
    return (
      <SectionCard
        title="Case Notes"
        trailing={
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              style={trailingButtonStyle('secondary')}
            >Cancel</button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              style={trailingButtonStyle('primary')}
            >{saving ? 'Saving…' : 'Save'}</button>
          </div>
        }
      >
        <textarea
          className="form-textarea"
          rows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add any context, strategy notes, follow-up reminders, or borrower communication summaries…"
          style={{ width: '100%', resize: 'vertical', minHeight: '7rem' }}
          disabled={saving}
        />
      </SectionCard>
    );
  }

  // ----- View mode -----
  return (
    <SectionCard
      title="Case Notes"
      trailing={
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: '#2563eb', fontSize: '0.7rem', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              }}
              title="Edit case notes"
            >
              <IconSystem.edit size={11} />
              {hasNotes ? 'Edit' : 'Add Notes'}
            </button>
          )}
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: '#2563eb', fontSize: '0.7rem', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              }}
            >
              {expanded ? 'Collapse' : 'Expand'}
              {expanded ? <IconSystem.chevronUp size={12} /> : <IconSystem.chevronDown size={12} />}
            </button>
          )}
        </div>
      }
    >
      {hasNotes ? (
        <div style={{
          fontSize: '0.85rem',
          color: 'var(--text-primary)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          maxHeight: showFull ? undefined : '4.5rem',
          overflow: showFull ? undefined : 'hidden',
          position: 'relative',
        }}>
          {notes}
          {!showFull && (
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              height: '1.5rem',
              background: 'linear-gradient(transparent, var(--bg-primary))',
              pointerEvents: 'none',
            }} />
          )}
        </div>
      ) : (
        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.85rem' }}>
          No notes recorded yet.{canEdit ? ' Use the “Add Notes” link above to capture context.' : ''}
        </div>
      )}
    </SectionCard>
  );
};

function trailingButtonStyle(variant: 'primary' | 'secondary'): React.CSSProperties {
  return {
    padding: '2px 10px',
    fontSize: '0.7rem', fontWeight: 600,
    borderRadius: 4,
    border: variant === 'primary' ? 'none' : '1px solid var(--form-border-color)',
    background: variant === 'primary' ? '#2563eb' : 'transparent',
    color: variant === 'primary' ? 'white' : 'var(--text-primary)',
    cursor: 'pointer',
  };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default SummarySection;
